import React, { useState, useEffect, memo, useRef } from 'react';
import { User, Menu, X, Phone, LogOut, ChevronDown, ChevronRight, Settings } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { getBackendUrl } from '../utils/apiConfig';
import { prefetchLinkProps } from '../utils/routePrefetch';

function NavLogo({ className = 'h-10 md:h-12 w-auto' }) {
  return (
    <img
      src="/chantilly-logo.jpg"
      alt="le Chantilly"
      className={`${className} rounded-lg`}
    />
  );
}

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerShellRef = useRef(null);
  const location = useLocation();

  // Structure de navigation - 4 catégories principales
  const navStructure = [
    {
      label: 'Le Club',
      items: [
        { path: '/', label: 'Accueil' },
        { path: '/licences', label: 'Licences & adhésion' },
        { path: '/restaurant', label: 'Restaurant & Bar' },
        { path: '/actualites', label: 'Actualités' },
        { path: '/contact', label: 'Contact & Accès' },
      ],
    },
    {
      label: 'Activités',
      items: [
        { path: '/padel', label: 'Padel' },
        { path: '/proshop', label: 'Proshop' },
        { path: '/fitness', label: 'Fitness' },
        { path: '/loisirs', label: 'Loisirs & Flippers' },
        { path: '/badminton', label: 'Badminton' },
        { path: '/basket', label: 'Basket 1v1' },
      ],
    },
    {
      label: 'Tournois',
      items: [
        { path: '/tournois', label: 'Vue d\'ensemble' },
        { path: '/tournois-padel', label: 'Tournois Padel' },
        { path: '/tournois-flippers', label: 'Tournois Flippers' },
      ],
    },
    {
      label: 'Entreprises',
      items: [
        { path: '/entreprises', label: 'Accueil B2B' },
        { path: '/seminaires', label: 'Séminaires' },
        { path: '/team-building', label: 'Team Building' },
        { path: '/privatisation', label: 'Privatisation' },
      ],
    },
  ];

  useEffect(() => {
    const validateAndSetAuth = async () => {
      const token = localStorage.getItem('auth_token');
      const user = localStorage.getItem('user_data');

      if (!token || !user) {
        setIsLoggedIn(false);
        setUserData(null);
        return;
      }

      setIsLoggedIn(true);
      setUserData(JSON.parse(user));

      try {
        const response = await fetch(`${getBackendUrl()}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user_data');
          setIsLoggedIn(false);
          setUserData(null);
        }
      } catch {
        /* réseau indisponible — garder session locale */
      }
    };

    const run = () => validateAndSetAuth();
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const id = window.requestIdleCallback(run, { timeout: 2500 });
      return () => window.cancelIdleCallback(id);
    }
    const t = setTimeout(run, 150);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onUserUpdated = (event) => {
      const next = event?.detail;
      if (next) {
        setIsLoggedIn(true);
        setUserData(next);
      } else {
        const raw = localStorage.getItem('user_data');
        if (raw) {
          try {
            setUserData(JSON.parse(raw));
            setIsLoggedIn(true);
          } catch {
            /* ignore */
          }
        }
      }
    };
    window.addEventListener('chantilly-user-updated', onUserUpdated);
    return () => window.removeEventListener('chantilly-user-updated', onUserUpdated);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setExpandedCategory(null);
    setActiveDropdown(null);
  }, [location.pathname]);

  useEffect(() => {
    const measure = () => {
      if (headerShellRef.current) {
        setHeaderHeight(headerShellRef.current.getBoundingClientRect().height);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const measure = () => {
      if (headerShellRef.current) {
        setHeaderHeight(headerShellRef.current.getBoundingClientRect().height);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('resize', measure);
      document.body.style.overflow = prev;
    };
  }, [isMenuOpen]);

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    setIsLoggedIn(false);
    setUserData(null);
    setIsMenuOpen(false);
    window.location.href = '/';
  };

  const toggleCategory = (idx) => {
    setExpandedCategory(expandedCategory === idx ? null : idx);
  };

  return (
    <>
      <div ref={headerShellRef} className="sticky top-0 z-50">
      {/* Bandeau info — discret */}
      <div className="bg-chantilly-green-dark text-chantilly-cream/90 py-2 px-4 text-center text-xs md:text-sm">
        <span className="hidden sm:inline">le Chantilly — Villeneuve-d&apos;Ascq · +200 tournois homologués / saison · </span>
        <a href="https://japadel.eu/" target="_blank" rel="noopener noreferrer" className="text-chantilly-gold font-semibold hover:underline">
          Inscriptions tournois padel →
        </a>
      </div>

      {/* Header principal — fond clair premium (sans backdrop-blur : casse position:fixed des enfants) */}
      <header className="bg-white text-chantilly-green-dark border-b border-chantilly-green/10 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-3">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0 flex items-center gap-3" {...prefetchLinkProps('/')}>
              <NavLogo className="h-10 md:h-12 w-auto" />
            </Link>

            {/* Desktop Navigation - Horizontal with dropdowns */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navStructure.map((category, idx) => (
                <div 
                  key={idx}
                  className="relative"
                  onMouseEnter={() => category.items && setActiveDropdown(idx)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  {category.items ? (
                    <>
                      <button className="flex items-center gap-1 px-4 py-2 font-medium text-chantilly-green-dark hover:text-chantilly-green transition-colors">
                        {category.label}
                        <ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === idx ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {/* Dropdown */}
                      {activeDropdown === idx && (
                        <div className="absolute top-full left-0 mt-0 w-52 bg-white border border-chantilly-green/10 rounded-xl shadow-xl overflow-hidden z-50">
                          {category.items.map((item, itemIdx) => (
                            <Link
                              key={itemIdx}
                              to={item.path}
                              className="block px-4 py-3 text-chantilly-green-dark hover:bg-chantilly-cream transition-colors border-b border-chantilly-green/5 last:border-0"
                              onClick={() => setActiveDropdown(null)}
                              {...prefetchLinkProps(item.path)}
                            >
                              {item.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link 
                      to={category.path}
                      className="px-4 py-2 font-medium text-chantilly-green-dark hover:text-chantilly-green transition-colors"
                    >
                      {category.label}
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            {/* Desktop Right buttons */}
            <div className="hidden lg:flex items-center space-x-3">
              <a href="https://playtomic.com/clubs/le-chantilly-club" target="_blank" rel="noopener noreferrer" title="Réservation terrains via Playtomic (hors site)">
                <Button className="bg-chantilly-green hover:bg-chantilly-green-dark text-white font-bold px-6 py-2 rounded-full text-sm shadow-sm">
                  Playtomic
                </Button>
              </a>
              {/* User Desktop */}
              {isLoggedIn ? (
                <div 
                  className="relative"
                  onMouseEnter={() => setActiveDropdown('user')}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <Button variant="ghost" className="text-chantilly-green-dark hover:text-chantilly-green flex items-center gap-1">
                    <User className="w-5 h-5" />
                    <span className="text-sm">{userData?.first_name}</span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                  
                  {activeDropdown === 'user' && (
                    <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-chantilly-green/15 rounded-xl shadow-xl overflow-hidden z-50">
                      <Link to="/mon-compte" className="block px-4 py-3 text-chantilly-green-dark hover:bg-chantilly-cream font-medium">
                        Mon compte
                      </Link>
                      {userData?.role === 'admin' && (
                        <>
                          <Link to="/admin/dashboard" className="flex items-center gap-2 px-4 py-3 text-chantilly-green hover:bg-chantilly-cream border-t border-chantilly-green/10 text-sm font-medium">
                            <Settings className="w-4 h-4" /> Super Admin
                          </Link>
                        </>
                      )}
                      <a href="tel:+33985008500" className="block px-4 py-3 text-chantilly-green-dark hover:bg-chantilly-cream border-t border-chantilly-green/10 text-sm">
                        09 85 00 85 00
                      </a>
                      <button onClick={handleLogout} className="block w-full text-left px-4 py-3 text-red-600 hover:bg-red-50 border-t border-chantilly-green/10 text-sm font-medium">
                        Déconnexion
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link to="/login">
                  <Button variant="outline" className="border-chantilly-green/30 text-chantilly-green-dark hover:bg-chantilly-cream text-sm rounded-full">
                    <User className="w-4 h-4 mr-1" /> Connexion
                  </Button>
                </Link>
              )}
            </div>

            {/* Mobile : logo + menu burger uniquement */}
            <div className="flex lg:hidden items-center gap-1">
              <a href="https://playtomic.com/clubs/le-chantilly-club" target="_blank" rel="noopener noreferrer" className="hidden sm:block" title="Via Playtomic">
                <Button className="bg-chantilly-green text-white hover:bg-chantilly-green-dark font-bold px-3 py-2 text-xs rounded-full">
                  Playtomic
                </Button>
              </a>

              <button
                type="button"
                aria-label={isMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
                aria-expanded={isMenuOpen}
                className="inline-flex items-center justify-center w-10 h-10 rounded-lg text-chantilly-green-dark hover:bg-chantilly-cream transition-colors"
                onClick={() => setIsMenuOpen((open) => !open)}
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </header>
      </div>

      {/* Menu mobile — hors du header pour que fixed couvre tout l'écran */}
      {isMenuOpen && (
        <>
          <button
            type="button"
            aria-label="Fermer le menu"
            className="lg:hidden fixed inset-0 z-[90] bg-chantilly-green-dark/40"
            style={{ top: headerHeight }}
            onClick={() => setIsMenuOpen(false)}
          />
          <div
            className="lg:hidden fixed left-0 right-0 bottom-0 z-[100] bg-chantilly-green-dark overflow-y-auto overscroll-contain"
            style={{ top: headerHeight }}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
          >
            <div className="container mx-auto px-4 py-6 pb-10">
              <nav className="space-y-2">
                {navStructure.map((category, idx) => (
                  <div key={idx} className="border-b border-chantilly-green-light/30">
                    {category.items ? (
                      <>
                        <button
                          onClick={() => toggleCategory(idx)}
                          className="w-full flex items-center justify-between py-4 text-left"
                        >
                          <span className="text-xl font-bold text-chantilly-cream">{category.label}</span>
                          <ChevronRight 
                            className={`w-6 h-6 text-chantilly-gold transition-transform duration-200 ${
                              expandedCategory === idx ? 'rotate-90' : ''
                            }`} 
                          />
                        </button>
                        
                        {expandedCategory === idx && (
                          <div className="pb-4 pl-4 space-y-1">
                            {category.items.map((item, itemIdx) => (
                              <Link
                                key={itemIdx}
                                to={item.path}
                                className="block py-3 px-4 text-chantilly-cream/80 hover:text-chantilly-gold hover:bg-chantilly-green rounded-lg transition-colors"
                                onClick={() => setIsMenuOpen(false)}
                                {...prefetchLinkProps(item.path)}
                              >
                                {item.label}
                              </Link>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <Link
                        to={category.path}
                        className="block py-4 text-xl font-bold text-chantilly-cream hover:text-chantilly-gold"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {category.label}
                      </Link>
                    )}
                  </div>
                ))}
              </nav>
              
              <div className="mt-8 space-y-3">
                <a href="https://playtomic.com/clubs/le-chantilly-club" target="_blank" rel="noopener noreferrer" className="block">
                  <Button className="w-full bg-chantilly-gold text-chantilly-green-dark hover:bg-chantilly-gold-dark font-bold py-4 text-lg">
                    Terrains sur Playtomic
                  </Button>
                </a>
                <Link to="/contact" className="block" onClick={() => setIsMenuOpen(false)}>
                  <Button variant="outline" className="w-full border-chantilly-cream text-chantilly-cream hover:bg-white hover:text-chantilly-green-dark py-4 text-lg">
                    Nous contacter
                  </Button>
                </Link>
                <Button 
                  className="w-full bg-chantilly-cream text-chantilly-green-dark hover:bg-white font-bold py-4 text-lg"
                  onClick={() => window.open('https://japadel.eu/', '_blank')}
                >
                  🏆 VOIR LES TOURNOIS
                </Button>
                <a href="tel:+33985008500" className="block">
                  <Button variant="outline" className="w-full border-green-500 text-green-500 hover:bg-green-500 hover:text-white py-4">
                    <Phone className="w-5 h-5 mr-2" /> 09 85 00 85 00
                  </Button>
                </a>
              </div>
              
              <div className="mt-6 pt-6 border-t border-gray-800">
                {isLoggedIn ? (
                  <div className="space-y-3">
                    <Link to="/mon-compte" onClick={() => setIsMenuOpen(false)}>
                      <Button variant="outline" className="w-full border-chantilly-cream text-chantilly-cream hover:bg-chantilly-cream hover:text-chantilly-green-dark py-3">
                        <User className="w-5 h-5 mr-2" /> Mon compte
                      </Button>
                    </Link>
                    {/* Admin links - visible only for admins (mobile) */}
                    {userData?.role === 'admin' && (
                      <Link to="/admin/dashboard" onClick={() => setIsMenuOpen(false)}>
                        <Button variant="outline" className="w-full border-chantilly-gold text-chantilly-gold hover:bg-chantilly-gold hover:text-chantilly-green-dark py-3">
                          <Settings className="w-5 h-5 mr-2" /> Super Admin
                        </Button>
                      </Link>
                    )}
                    <Button 
                      variant="outline" 
                      className="w-full border-red-500 text-red-500 hover:bg-red-500 hover:text-white py-3"
                      onClick={handleLogout}
                    >
                      <LogOut className="w-5 h-5 mr-2" /> Déconnexion
                    </Button>
                  </div>
                ) : (
                  <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                    <Button variant="outline" className="w-full border-chantilly-cream text-chantilly-cream hover:bg-chantilly-cream hover:text-chantilly-green-dark py-3">
                      <User className="w-5 h-5 mr-2" /> Connexion / Inscription
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default memo(Header);
